import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { isMotionNote } from "../lib/plainEnglish";

const isApproved = (s) => (s.approval_state || "").toUpperCase() === "APPROVED";

// One place that derives every count shown on the project page.
export function deriveProject({ detail, scenes, renderStatus, cost, progress, preflight }) {
    const m = detail.module;
    const versionId = detail.current_version?.id || m.current_version_id;
    const latest = renderStatus?.latest_render || null;
    const settingsChanged = !!latest && !!m.settings_changed_at && m.settings_changed_at > (latest.created_at || "");
    const latestIsCurrent = !!latest && (!versionId || latest.version_id === versionId) && !settingsChanged;
    const approved = scenes.filter(isApproved).length;
    const cov = preflight?.image_coverage;
    const seen = new Set();
    const all = [...(preflight?.issues || []), ...(preflight?.warnings || [])].filter((w) => {
        const k = `${w.scene_id || ""}|${w.kind || w.code || w.message}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
    });
    const warnings = all.filter((w) => !isMotionNote(w));
    const notes = all.filter(isMotionNote);
    return {
        module: m,
        versionId,
        scenes,
        scenesTotal: scenes.length,
        approved,
        needsReview: scenes.length - approved,
        videoScenes: renderStatus?.video_scenes?.count ?? null,
        videoScenesMeasured: !!renderStatus?.video_scenes?.measured,
        settingsChanged,
        durationS: latestIsCurrent ? latest.duration_s : detail.estimated_duration_s,
        durationMeasured: latestIsCurrent,
        images: cov ? { used: cov.used, total: cov.total } : null,
        buildsCompleted: (cost?.builds || []).length,
        running: !!renderStatus?.current_job,
        currentJob: renderStatus?.current_job || null,
        latest,
        latestIsCurrent,
        published: m.status === "PUBLISHED",
        storyboardApproved: m.status === "APPROVED" || m.status === "PUBLISHED",
        preflightOk: !!preflight?.ok,
        warnings,
        notes,
        documentId: m.document_id,
        documentTitle: detail.document_title,
    };
}

export default function useProjectData(id) {
    const [project, setProject] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            const [d, rs, cs, bp] = await Promise.all([
                api.get(`/modules/${id}`),
                api.get(`/modules/${id}/render-status`).catch(() => ({ data: null })),
                api.get(`/modules/${id}/cost-summary`).catch(() => ({ data: null })),
                api.get(`/modules/${id}/build-progress`).catch(() => ({ data: null })),
            ]);
            const vid = d.data.current_version?.id;
            const [sc, pf] = vid ? await Promise.all([
                api.get(`/modules/${id}/versions/${vid}/scenes?fields=lite`).catch(() => ({ data: [] })),
                api.post(`/modules/${id}/render-preflight`).catch(() => ({ data: null })),
            ]) : [{ data: [] }, { data: null }];
            setProject(deriveProject({
                detail: d.data, scenes: sc.data || [], renderStatus: rs.data,
                cost: cs.data, progress: bp.data, preflight: pf.data,
            }));
        } catch (e) { setError(e); }
    }, [id]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!project?.running) return undefined;
        const t = setInterval(load, 10000);
        return () => clearInterval(t);
    }, [project?.running, load]);

    return { project, error, reload: load };
}
